import { IdentifyResult } from '../../shared/identify';
import type { PauseReason } from './identifyQueue';

export type PostOutcome =
  | { kind: 'ok'; result: IdentifyResult }
  | { kind: 'unavailable'; reason: Exclude<PauseReason, 'daily_limit'> }
  | { kind: 'daily_limit'; retryAfterMs: number }
  | { kind: 'transient' | 'upload_timeout' | 'bad_output' | 'permanent' };

export interface Reply {
  status: number;
  text: string;
}

export type Send = (body: string, signal: AbortSignal, onBodySent: () => void) => Promise<Reply>;

// Node answers a body still arriving after its 300 s requestTimeout with 408 and resets the
// socket, which usually drops the 408 before the browser reads it.
const SLOW_UPLOAD_MS = 60_000;

// Node bounds the upload at 300 s, and Apache's ProxyTimeout the wait for a reply (a model
// call takes at most 240 s) at 300 s, so the clock restarts when the body is sent.
const PHASE_TIMEOUT_MS = 300_000;

// These answer the same for every job, so the queue pauses instead of retrying each one.
const PAUSE_STATUSES = new Map<number, Exclude<PauseReason, 'daily_limit'>>([
  [401, 'signed_out'],
  [429, 'rate_limited'],
  [503, 'service_unavailable'],
]);

export async function postIdentify(
  images: string[],
  send: Send = xhrSend,
  now: () => number = Date.now,
): Promise<PostOutcome> {
  const started = now();
  const controller = new AbortController();
  let timer = setTimeout(() => controller.abort(), PHASE_TIMEOUT_MS);
  let bodySent = false;
  let reply: Reply;
  try {
    reply = await send(JSON.stringify({ images }), controller.signal, () => {
      bodySent = true;
      clearTimeout(timer);
      timer = setTimeout(() => controller.abort(), PHASE_TIMEOUT_MS);
    });
  } catch {
    if (navigator.onLine === false) return { kind: 'unavailable', reason: 'offline' };
    // Online, a rejected request may repeat for this job forever, so it counts toward the cap.
    // Once the body is out, a late drop is the network or the device sleeping, not the uplink.
    const slowUpload = !bodySent && now() - started >= SLOW_UPLOAD_MS;
    return slowUpload ? { kind: 'upload_timeout' } : { kind: 'transient' };
  } finally {
    clearTimeout(timer);
  }
  const { status } = reply;
  if (status === 408) return { kind: 'upload_timeout' };
  const body = parseJson(reply.text);
  if (status >= 200 && status < 300) {
    const parsed = IdentifyResult.safeParse(body);
    return parsed.success ? { kind: 'ok', result: parsed.data } : { kind: 'bad_output' };
  }
  const error = (body as { error?: string } | undefined)?.error;
  if (status === 429 && error === 'daily_limit')
    return { kind: 'daily_limit', retryAfterMs: parseRetryAfterMs(body) };
  const reason = PAUSE_STATUSES.get(status);
  if (reason) return { kind: 'unavailable', reason };
  // A Go 4xx (other than 401, 403, 429) may be provider-wide (a retired model id), so it gets the bad-output budget.
  if (status === 400 && error === 'image_rejected') return { kind: 'bad_output' };
  if (status === 400 || status === 413) return { kind: 'permanent' };
  // A proxy's own 502 means the backend is down, not that the model misbehaved.
  if (status === 502 && error === 'bad_model_output') return { kind: 'bad_output' };
  return { kind: 'transient' };
}

export class ProtocolError extends Error {}

function parseRetryAfterMs(body: unknown): number {
  const raw = (body as { retryAfterMs?: unknown } | undefined)?.retryAfterMs;
  if (typeof raw !== 'number' || !Number.isInteger(raw) || raw <= 0)
    throw new ProtocolError(`daily_limit 429 without a valid retryAfterMs: ${String(raw)}`);
  return raw;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

// fetch has no portable signal that the request body has gone out (streamed request bodies are
// Chromium-only and need HTTP/2), while XHR fires upload `load` in every browser.
export function xhrSend(body: string, signal: AbortSignal, onBodySent: () => void): Promise<Reply> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/identify');
    xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', 'application/json');
    xhr.upload.addEventListener('load', onBodySent);
    xhr.addEventListener('load', () => resolve({ status: xhr.status, text: xhr.responseText }));
    for (const type of ['error', 'abort', 'timeout']) {
      xhr.addEventListener(type, () => reject(new TypeError(`identify request ${type}`)));
    }
    signal.addEventListener('abort', () => xhr.abort());
    xhr.send(body);
  });
}

export async function blobToBase64(blob: Blob): Promise<string> {
  // jsdom's Blob has no arrayBuffer().
  const bytes = new Uint8Array(await readAsArrayBuffer(blob));
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

function readAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}
